import type { Contact } from "@/api/queries";
import { WhatsAppButton } from "@/inbox/WhatsAppButton";
import { formatPhone } from "@/lib/format";
import { SendMailButton } from "@/mail/SendMailButton";
import { CallButton } from "@/telephony/CallButton";

/**
 * The ways to reach one person: a row per number and per address, and beside
 * each the button that does it.
 *
 * Its own component because the contact card of screen 04 and the event modal
 * of screen 05 both draw it, and the rules it carries — the do-not-call list,
 * WhatsApp's own consent, a disabled contact — are not ones two screens may
 * disagree about.
 *
 * A disabled contact keeps its rows, dimmed and with nothing to reach them by.
 */
export function ContactReach({ contact }: { contact: Contact }) {
  const disabled = Boolean(contact.disabled_at);
  const dimmed = disabled ? "opacity-60" : "";

  return (
    <>
      {contact.phones.length > 0 ? (
        <div className={`mt-3.5 flex flex-col gap-2 ${dimmed}`}>
          {contact.phones.map((phone) => (
            <div
              key={phone.id}
              className="flex flex-wrap items-center justify-between gap-3 border-t border-rule pt-2"
            >
              <div className="flex items-baseline gap-2.5">
                <span className="font-display text-sm font-extrabold tabular-nums">
                  {formatPhone(phone.phone)}
                </span>
                <span className="text-[11px] tracking-[0.08em] text-mute-600 uppercase">
                  {phone.kind_display}
                </span>
              </div>
              {/* Nothing to reach a disabled contact by: the number is on
                  file to be read, not dialled. */}
              {disabled ? null : (
                <div className="flex flex-wrap items-center gap-2">
                  {/* The do-not-call list is consulted server-side before
                      every dial; hiding the button on a suppressed number is
                      the UI half of the same rule (.docs/06 §4). */}
                  {phone.is_suppressed ? null : (
                    // `phone`, not `number`: a `ContactPhone` pk is what puts
                    // the call on this contact's timeline instead of leaving
                    // it as a dialled string nobody can find again.
                    <CallButton phone={phone.id} label={contact.name} />
                  )}
                  {/* Outside the suppression branch on purpose: the
                      do-not-call list is about calls, and WhatsApp answers to
                      its own consent, which the button reads. */}
                  <WhatsAppButton contact={contact} phone={phone} />
                </div>
              )}
            </div>
          ))}
        </div>
      ) : null}

      {/* Addresses get the same row a number does: the way to reach the
          person, and beside it the button that does. Every address, not the
          first — the modal takes as many as the person has, and one the card
          hides is one nobody writes to. */}
      {contact.emails.length > 0 ? (
        <div className={`mt-3.5 flex flex-col gap-2 ${dimmed}`}>
          {contact.emails.map((email) => (
            <div
              key={email.id}
              className="flex flex-wrap items-center justify-between gap-3 border-t border-rule pt-2"
            >
              <span className="min-w-0 text-sm break-all">{email.email}</span>
              {disabled ? null : (
                <SendMailButton contact={contact} address={email.email} compact />
              )}
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}
